import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  BarChart3,
  LogOut,
  Moon,
  Settings,
  Sun,
} from 'lucide-react';
import { Sidebar, SidebarBody, SidebarLink, useSidebar } from '../ui/sidebar';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

// ----- Logo components -----

function VasariLogo({ isDark }) {
  return (
    <div className="flex items-center space-x-2 py-1">
      <svg width="28" height="28" viewBox="0 0 8689 9614" fill={isDark ? '#e5e7eb' : '#1c1917'} className="flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
        <rect x="5905" y="1006" width="1935" height="3121" transform="rotate(90 5905 1006)" />
        <rect x="4792" y="0" width="2819" height="895" transform="rotate(90 4792 0)" />
        <rect x="5566" y="548" width="916" height="2445" transform="rotate(90 5566 548)" />
        <rect x="3123" y="2900" width="1935" height="3121" transform="rotate(90 3123 2900)" />
        <rect x="2010" y="1894" width="3371" height="895" transform="rotate(90 2010 1894)" />
        <rect x="2784" y="2442" width="916" height="2445" transform="rotate(90 2784 2442)" />
        <rect x="8689" y="2928" width="1935" height="3121" transform="rotate(90 8689 2928)" />
        <rect x="7576" y="1922" width="3371" height="895" transform="rotate(90 7576 1922)" />
        <rect x="8350" y="2470" width="916" height="2445" transform="rotate(90 8350 2470)" />
        <rect x="2782" y="8608" width="1935" height="3121" transform="rotate(-90 2782 8608)" />
        <rect x="3895" y="9614" width="2900" height="895" transform="rotate(-90 3895 9614)" />
        <rect x="3121" y="9066" width="916" height="2445" transform="rotate(-90 3121 9066)" />
        <rect x="5566" y="6742" width="1935" height="3121" transform="rotate(-90 5566 6742)" />
        <rect x="6679" y="7748" width="3371" height="895" transform="rotate(-90 6679 7748)" />
        <rect x="5905" y="7200" width="916" height="2445" transform="rotate(-90 5905 7200)" />
        <rect y="6714" width="1935" height="3121" transform="rotate(-90 0 6714)" />
        <rect x="1113" y="7720" width="3371" height="895" transform="rotate(-90 1113 7720)" />
        <rect x="339" y="7172" width="916" height="2445" transform="rotate(-90 339 7172)" />
      </svg>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-xl font-semibold text-stone-900 dark:text-white font-mono whitespace-pre"
      >
        Vasari
      </motion.span>
    </div>
  );
}

function VasariLogoIcon({ isDark }) {
  return (
    <div className="py-1">
      <svg width="28" height="28" viewBox="0 0 8689 9614" fill={isDark ? '#e5e7eb' : '#1c1917'} className="flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
        <rect x="5905" y="1006" width="1935" height="3121" transform="rotate(90 5905 1006)" />
        <rect x="4792" y="0" width="2819" height="895" transform="rotate(90 4792 0)" />
        <rect x="5566" y="548" width="916" height="2445" transform="rotate(90 5566 548)" />
        <rect x="3123" y="2900" width="1935" height="3121" transform="rotate(90 3123 2900)" />
        <rect x="2010" y="1894" width="3371" height="895" transform="rotate(90 2010 1894)" />
        <rect x="2784" y="2442" width="916" height="2445" transform="rotate(90 2784 2442)" />
        <rect x="8689" y="2928" width="1935" height="3121" transform="rotate(90 8689 2928)" />
        <rect x="7576" y="1922" width="3371" height="895" transform="rotate(90 7576 1922)" />
        <rect x="8350" y="2470" width="916" height="2445" transform="rotate(90 8350 2470)" />
        <rect x="2782" y="8608" width="1935" height="3121" transform="rotate(-90 2782 8608)" />
        <rect x="3895" y="9614" width="2900" height="895" transform="rotate(-90 3895 9614)" />
        <rect x="3121" y="9066" width="916" height="2445" transform="rotate(-90 3121 9066)" />
        <rect x="5566" y="6742" width="1935" height="3121" transform="rotate(-90 5566 6742)" />
        <rect x="6679" y="7748" width="3371" height="895" transform="rotate(-90 6679 7748)" />
        <rect x="5905" y="7200" width="916" height="2445" transform="rotate(-90 5905 7200)" />
        <rect y="6714" width="1935" height="3121" transform="rotate(-90 0 6714)" />
        <rect x="1113" y="7720" width="3371" height="895" transform="rotate(-90 1113 7720)" />
        <rect x="339" y="7172" width="916" height="2445" transform="rotate(-90 339 7172)" />
      </svg>
    </div>
  );
}

// ----- Mobile header logo (always visible) -----

function MobileLogo({ isDark, onClick }) {
  return (
    <button onClick={onClick} className="flex items-center space-x-2">
      <svg width="28" height="28" viewBox="0 0 8689 9614" fill={isDark ? '#e5e7eb' : '#1c1917'} className="flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
        <rect x="5905" y="1006" width="1935" height="3121" transform="rotate(90 5905 1006)" />
        <rect x="4792" y="0" width="2819" height="895" transform="rotate(90 4792 0)" />
        <rect x="5566" y="548" width="916" height="2445" transform="rotate(90 5566 548)" />
        <rect x="3123" y="2900" width="1935" height="3121" transform="rotate(90 3123 2900)" />
        <rect x="2010" y="1894" width="3371" height="895" transform="rotate(90 2010 1894)" />
        <rect x="2784" y="2442" width="916" height="2445" transform="rotate(90 2784 2442)" />
        <rect x="8689" y="2928" width="1935" height="3121" transform="rotate(90 8689 2928)" />
        <rect x="7576" y="1922" width="3371" height="895" transform="rotate(90 7576 1922)" />
        <rect x="8350" y="2470" width="916" height="2445" transform="rotate(90 8350 2470)" />
        <rect x="2782" y="8608" width="1935" height="3121" transform="rotate(-90 2782 8608)" />
        <rect x="3895" y="9614" width="2900" height="895" transform="rotate(-90 3895 9614)" />
        <rect x="3121" y="9066" width="916" height="2445" transform="rotate(-90 3121 9066)" />
        <rect x="5566" y="6742" width="1935" height="3121" transform="rotate(-90 5566 6742)" />
        <rect x="6679" y="7748" width="3371" height="895" transform="rotate(-90 6679 7748)" />
        <rect x="5905" y="7200" width="916" height="2445" transform="rotate(-90 5905 7200)" />
        <rect y="6714" width="1935" height="3121" transform="rotate(-90 0 6714)" />
        <rect x="1113" y="7720" width="3371" height="895" transform="rotate(-90 1113 7720)" />
        <rect x="339" y="7172" width="916" height="2445" transform="rotate(-90 339 7172)" />
      </svg>
      <span className="text-xl font-semibold text-stone-900 dark:text-white font-mono">Vasari</span>
    </button>
  );
}

// ----- SidebarBottomActions (theme + logout, icon-only buttons) -----

function BottomActions({ isDark, toggleDark, onLogout, onSettings }) {
  const { open, animate } = useSidebar();
  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={onSettings}
        className="flex items-center gap-2.5 px-2 py-2 rounded-md text-stone-500 dark:text-gray-400 hover:bg-stone-100 dark:hover:bg-gray-800 hover:text-stone-900 dark:hover:text-white transition-colors"
        title="Settings"
      >
        <Settings className="w-4 h-4 flex-shrink-0" />
        <motion.span
          animate={{
            display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
            opacity: animate ? (open ? 1 : 0) : 1,
          }}
          className="text-sm whitespace-pre !p-0 !m-0"
        >
          Settings
        </motion.span>
      </button>
      <button
        onClick={toggleDark}
        className="flex items-center gap-2.5 px-2 py-2 rounded-md text-stone-500 dark:text-gray-400 hover:bg-stone-100 dark:hover:bg-gray-800 hover:text-stone-900 dark:hover:text-white transition-colors"
        title={isDark ? 'Light mode' : 'Dark mode'}
      >
        {isDark ? <Sun className="w-4 h-4 flex-shrink-0" /> : <Moon className="w-4 h-4 flex-shrink-0" />}
        <motion.span
          animate={{
            display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
            opacity: animate ? (open ? 1 : 0) : 1,
          }}
          className="text-sm whitespace-pre !p-0 !m-0"
        >
          {isDark ? 'Light mode' : 'Dark mode'}
        </motion.span>
      </button>
      <button
        onClick={onLogout}
        className="flex items-center gap-2.5 px-2 py-2 rounded-md text-stone-500 dark:text-gray-400 hover:bg-stone-100 dark:hover:bg-gray-800 hover:text-stone-900 dark:hover:text-white transition-colors"
        title="Log out"
      >
        <LogOut className="w-4 h-4 flex-shrink-0" />
        <motion.span
          animate={{
            display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
            opacity: animate ? (open ? 1 : 0) : 1,
          }}
          className="text-sm whitespace-pre !p-0 !m-0"
        >
          Log out
        </motion.span>
      </button>
    </div>
  );
}

// ----- UserAvatar row -----

function UserAvatarRow({ user }) {
  const { open, animate } = useSidebar();
  const initial = user?.full_name?.charAt(0).toUpperCase() ?? '?';
  return (
    <div className="flex items-center gap-2.5 px-2 py-2">
      <div className="w-7 h-7 rounded-full bg-stone-900 dark:bg-gray-600 text-white flex items-center justify-center text-xs font-bold font-mono flex-shrink-0">
        {initial}
      </div>
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="text-sm text-stone-600 dark:text-gray-400 whitespace-pre !p-0 !m-0 truncate max-w-[140px]"
      >
        {user?.full_name ?? ''}
      </motion.span>
    </div>
  );
}

// ----- AppSidebar -----

function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDark, toggleDark } = useTheme();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const links = [
    {
      label: 'Dashboard',
      href: '/dashboard',
      active: isActive('/dashboard'),
      icon: <LayoutDashboard className="w-4 h-4 flex-shrink-0 text-stone-500 dark:text-gray-400" />,
    },
    {
      label: 'Clients',
      href: '/clients',
      active: isActive('/clients'),
      icon: <Users className="w-4 h-4 flex-shrink-0 text-stone-500 dark:text-gray-400" />,
    },
    {
      label: 'Documents',
      href: '/documents',
      active: isActive('/documents'),
      icon: <FileText className="w-4 h-4 flex-shrink-0 text-stone-500 dark:text-gray-400" />,
    },
    {
      label: 'Portfolios',
      href: '/portfolio',
      active: isActive('/portfolio'),
      icon: <BarChart3 className="w-4 h-4 flex-shrink-0 text-stone-500 dark:text-gray-400" />,
    },
  ];

  return (
    <Sidebar open={open} setOpen={setOpen}>
      {/* Mobile top bar */}
      <div
        className="md:hidden h-12 px-4 flex items-center justify-between w-full border-b border-stone-200/50 dark:border-gray-700/50 bg-white/80 dark:bg-gray-900/80"
        style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      >
        <MobileLogo isDark={isDark} onClick={() => navigate('/dashboard')} />
        {/* Mobile hamburger is rendered by MobileSidebar — but we override with custom top bar */}
      </div>

      <SidebarBody className="border-r border-stone-200/50 dark:border-gray-700/50 bg-white dark:bg-gray-900 justify-between gap-10">
        {/* Top: Logo + Nav */}
        <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          {open ? (
            <VasariLogo isDark={isDark} />
          ) : (
            <VasariLogoIcon isDark={isDark} />
          )}
          <div className="mt-6 flex flex-col gap-0.5">
            {links.map((link) => (
              <SidebarLink key={link.href} link={link} onClick={() => setOpen(false)} />
            ))}
          </div>
        </div>

        {/* Bottom: User + actions */}
        <div className="flex flex-col gap-1">
          <div className="border-t border-stone-200/50 dark:border-gray-700/50 pt-3 flex flex-col gap-1">
            <UserAvatarRow user={user} />
            <BottomActions isDark={isDark} toggleDark={toggleDark} onLogout={handleLogout} onSettings={() => { navigate('/settings'); setOpen(false); }} />
          </div>
        </div>
      </SidebarBody>
    </Sidebar>
  );
}

export default AppSidebar;
